import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

import { isAdmin, useSession } from '@/features/auth';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field, Skeleton, useToastStore } from '@/shared/ui';

import type { TeamSummary, UserSummary } from '../model/types';
import {
  useAddTeamMembers,
  useCreateTeam,
  useDeleteTeam,
  useRemoveTeamMember,
  useUpdateTeam,
  useTeamMembers,
  useTeams,
  useUsers,
} from '../queries/useIdentity';
import styles from './Settings.module.css';
import { suggestTeamKey } from '../model/teamKey';
import { SectionHead } from './SettingsScreen';

/**
 * Zespoły — **lista po lewej, wybrany zespół po prawej** (spec sek. 10: zespoły,
 * użytkownicy, członkostwo M:N).
 *
 * ⭐ Układ master–detail, a nie lista z rozwijaniem, bo praca ze składem jest **porównawcza**:
 * admin przerzuca ludzi między zespołami i musi widzieć, co robi, nie tracąc z oczu reszty.
 *
 * ⚠ Lista jest widoczna dla **każdego zalogowanego**, zmiany tylko dla admina — tak stanowi
 * `TeamsController` i spec 9.2: *„kto jest w jakim zespole, to wiedza organizacyjna,
 * nie sekret"*. Ukryte są same akcje, nie dane.
 */
export function TeamsSection() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const { data: user } = useSession();
  const admin = isAdmin(user);
  const teams = useTeams();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = teams.data?.find((team) => team.id === selectedId) ?? null;

  return (
    <>
      <SectionHead titleKey="settings.section.teams" leadKey="settings.teams.lead" />

      {admin ? <CreateTeamForm onCreated={(team) => setSelectedId(team.id)} /> : null}

      <div className={styles['split']}>
        <section className={styles['card']}>
          <header className={styles['cardHead']}>
            <h2 className={styles['cardTitle']}>{t('settings.teams.title')}</h2>
          </header>

          {teams.isPending ? (
            <div className={styles['cardBody']}>
              <div className={styles['loading']} aria-busy="true">
                <Skeleton height="2.5rem" announce />
                <Skeleton height="2.5rem" />
              </div>
            </div>
          ) : teams.isError ? (
            <div className={styles['cardBody']}>
              <div className={styles['failure']} role="alert">
                <p>{describeError(teams.error)}</p>
                <Button onClick={() => void teams.refetch()}>{t('common.action.retry')}</Button>
              </div>
            </div>
          ) : teams.data.length === 0 ? (
            <p className={styles['empty']}>{t('settings.teams.empty')}</p>
          ) : (
            <ul className={styles['pickList']}>
              {teams.data.map((team) => (
                <li key={team.id}>
                  <button
                    type="button"
                    className={`${styles['pick']} ${team.id === selectedId ? styles['pickActive'] : ''}`}
                    onClick={() => setSelectedId(team.id)}
                  >
                    <span className={styles['pickName']}>{team.name}</span>
                    <span className={styles['pickMeta']}>
                      {t('settings.teams.memberCount', { count: team.memberCount })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {selected ? (
          <TeamDetail
            key={selected.id}
            team={selected}
            admin={admin}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <section className={styles['card']}>
            <p className={styles['empty']}>{t('settings.teams.pickOne')}</p>
          </section>
        )}
      </div>
    </>
  );
}

/**
 * Wybrany zespół: nazwa, za co odpowiada, skład i dopisywanie ludzi.
 *
 * `key={team.id}` u wołającego przemontowuje ten komponent przy zmianie zespołu — bez tego
 * zaznaczenie w wyszukiwarce osób przenosiłoby się na inny zespół.
 */
function TeamDetail({
  team,
  admin,
  onDeleted,
}: {
  team: TeamSummary;
  admin: boolean;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const members = useTeamMembers(team.id);
  const remove = useRemoveTeamMember();
  const update = useUpdateTeam();
  const deleteTeam = useDeleteTeam();

  const [name, setName] = useState(team.name);
  const [key, setKey] = useState(team.key ?? '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const bezZmian = name.trim() === team.name && key.trim() === (team.key ?? '');

  function onSave(event: FormEvent) {
    event.preventDefault();

    if (name.trim() === '' || key.trim() === '' || bezZmian) {
      return;
    }

    update.mutate(
      { teamId: team.id, name: name.trim(), key: key.trim() },
      {
        onSuccess: (zapisany) =>
          show({ message: t('settings.teams.renamed', { name: zapisany.name }), tone: 'success' }),
        onError: (error) => show({ message: describeError(error), tone: 'danger' }),
      },
    );
  }

  return (
    <section className={styles['card']}>
      <header className={styles['cardHead']}>
        <h2 className={styles['cardTitle']}>{team.name}</h2>

        <span className={styles['toolbar']}>
          {/*
            „Za co ten zespół odpowiada" — ta sama liczba, która zdecyduje, czy usunięcie
            się uda. Pokazana zawsze, nie dopiero przy odmowie: admin ma wiedzieć, czym
            zespół jest, zanim spróbuje go skasować.
          */}
          <span className={styles['badge']}>
            {t('settings.teams.owns', { count: team.ownedElementCount })}
          </span>
        </span>
      </header>

      <div className={styles['cardBody']}>
        {admin ? (
          <form className={styles['stackedForm']} onSubmit={onSave} noValidate>
            {/*
              ⚠ Pola wyrównane DO GÓRY, przycisk w osobnym wierszu. Przy wyrównaniu do dołu
              etykiety „Nazwa zespołu" i „Klucz" stały na różnych wysokościach, bo podpowiedź
              pod kluczem jest dłuższa i zawija się na dwie linie.
            */}
            <div className={styles['fieldRow']}>
              <Field
                label={t('settings.teams.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />

              {/*
                ⚠ Klucz stoi obok nazwy, a nie zamiast niej: to DWIE różne rzeczy. Nazwa jest dla
                ludzi i wolno ją poprawić, klucz jest tożsamością dla pliku importu — dlatego
                podpowiedź pod polem mówi wprost, co się stanie po jego zmianie.
              */}
              <Field
                label={t('settings.teams.key')}
                value={key}
                hint={t('settings.teams.keyChangeHint')}
                onChange={(event) => setKey(event.target.value)}
              />
            </div>

            <div className={styles['formActions']}>
              <Button type="submit" variant="ghost" disabled={update.isPending || bezZmian}>
                {t('settings.teams.rename')}
              </Button>
            </div>
          </form>
        ) : null}
      </div>

      <div className={styles['cardBody']}>
        <h3 className={styles['subTitle']}>{t('settings.teams.members')}</h3>

        {members.isPending ? (
          <div className={styles['loading']} aria-busy="true">
            <Skeleton height="2rem" announce />
          </div>
        ) : members.isError ? (
          <div className={styles['failure']} role="alert">
            <p>{describeError(members.error)}</p>
          </div>
        ) : members.data.length === 0 ? (
          <p className={styles['emptyInline']}>{t('settings.teams.noMembers')}</p>
        ) : (
          <ul className={styles['chips']}>
            {members.data.map((member) => (
              <li key={member.id} className={styles['chip']}>
                {member.displayName}

                {admin ? (
                  <button
                    type="button"
                    className={styles['chipRemove']}
                    aria-label={t('settings.teams.removeMember', { name: member.displayName })}
                    onClick={() =>
                      remove.mutate(
                        { teamId: team.id, userId: member.id },
                        {
                          onError: (error) =>
                            show({ message: describeError(error), tone: 'danger' }),
                        },
                      )
                    }
                  >
                    <X size={13} aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {admin ? (
        <>
          <div className={styles['cardBody']}>
            <AddMembersForm teamId={team.id} memberIds={members.data?.map((m) => m.id) ?? []} />
          </div>

          <footer className={styles['cardFoot']}>
            {confirmingDelete ? (
              <>
                <Button
                  variant="danger"
                  disabled={deleteTeam.isPending}
                  onClick={() =>
                    deleteTeam.mutate(team.id, {
                      onSuccess: () => {
                        onDeleted();
                        show({
                          message: t('settings.teams.deleted', { name: team.name }),
                          tone: 'success',
                        });
                      },
                      // ⛔ Odmowa jest tu NORMALNYM wynikiem: zespół będący właścicielem
                      // elementów nie może zniknąć, bo zostawiłby wiszącą własność.
                      onError: (error) => {
                        setConfirmingDelete(false);
                        show({ message: describeError(error), tone: 'danger' });
                      },
                    })
                  }
                >
                  {t('settings.teams.deleteConfirm')}
                </Button>

                <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  {t('common.action.cancel')}
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
                {t('settings.teams.delete')}
              </Button>
            )}
          </footer>
        </>
      ) : null}
    </section>
  );
}

/**
 * Dopisywanie ludzi: wyszukiwarka + zaznaczanie + **jedno żądanie na całą paczkę**.
 *
 * ⭐ To jest sedno „przyjemnego dodawania": zaznaczam kilka osób, klikam raz. Dziesięć
 * osobnych żądań i dziesięć unieważnień pamięci podręcznej dawałoby listę migającą przy
 * każdym kliknięciu.
 *
 * ⚠ Lista kandydatów **odsiewa tych, którzy już są w zespole** — pokazywanie ich znaczyłoby,
 * że kliknięcie coś zmieni, a nie zmieni nic.
 */
/**
 * Ilu kandydatów ściągamy pod picker. ⚠ Więcej niż widocznych dwunastu, bo część odpadnie
 * lokalnie jako już będąca w zespole — inaczej pełny zespół dawałby pustą listę mimo trafień.
 */
const CANDIDATE_LIMIT = 50;

function AddMembersForm({ teamId, memberIds }: { teamId: number; memberIds: readonly number[] }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const add = useAddTeamMembers();

  const [query, setQuery] = useState('');
  const [phrase, setPhrase] = useState('');
  const [picked, setPicked] = useState<ReadonlySet<number>>(new Set());

  // ⚠ Szukanie przeniesione NA SERWER razem ze stronicowaniem listy kont (2026-09-03).
  //    Filtrowanie po stronie przeglądarki działało, dopóki `useUsers` oddawało wszystkich —
  //    po stronicowaniu przeszukiwałoby wyłącznie pierwszą stronę, czyli po cichu ukryłoby
  //    większość organizacji przed adminem układającym zespół.
  useEffect(() => {
    const handle = setTimeout(() => setPhrase(query.trim()), 250);

    return () => clearTimeout(handle);
  }, [query]);

  // Aktywni, bo do zespołu nie ma po co dopisywać kogoś, kogo admin jeszcze nie wpuścił.
  const users = useUsers({ state: 'active', q: phrase, offset: 0, limit: CANDIDATE_LIMIT });

  // ⚠ Odsiew członków zostaje LOKALNY — kto już jest w tym zespole, wie tylko ten ekran.
  const candidates = (users.data?.items ?? []).filter(
    (candidate) => !memberIds.includes(candidate.id),
  );

  function toggle(id: number) {
    setPicked((previous) => {
      const next = new Set(previous);

      if (!next.delete(id)) {
        next.add(id);
      }

      return next;
    });
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    if (picked.size === 0) {
      return;
    }

    add.mutate(
      { teamId, userIds: [...picked] },
      {
        onSuccess: () => {
          show({ message: t('settings.teams.membersAdded', { count: picked.size }), tone: 'success' });
          setPicked(new Set());
          setQuery('');
        },
        onError: (error) => show({ message: describeError(error), tone: 'danger' }),
      },
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <h3 className={styles['subTitle']}>{t('settings.teams.addMembers')}</h3>

      <Field
        label={t('settings.teams.findPeople')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        hint={t('settings.teams.findPeopleHint')}
      />

      {users.isPending ? (
        <div className={styles['loading']} aria-busy="true">
          <Skeleton height="2rem" announce />
        </div>
      ) : candidates.length === 0 ? (
        <p className={styles['emptyInline']}>{t('settings.teams.noCandidates')}</p>
      ) : (
        <ul className={styles['pickList']}>
          {candidates.slice(0, 12).map((candidate) => (
            <li key={candidate.id}>
              <button
                type="button"
                className={`${styles['pick']} ${picked.has(candidate.id) ? styles['pickActive'] : ''}`}
                aria-pressed={picked.has(candidate.id)}
                onClick={() => toggle(candidate.id)}
              >
                <span className={styles['pickName']}>{candidate.displayName}</span>
                <span className={styles['pickMeta']}>
                  {picked.has(candidate.id) ? (
                    <Check size={14} aria-hidden="true" />
                  ) : (
                    <Teams user={candidate} />
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles['inlineForm']}>
        <Button type="submit" variant="primary" disabled={picked.size === 0 || add.isPending}>
          {t('settings.teams.addPicked', { count: picked.size })}
        </Button>
      </div>
    </form>
  );
}

/** W jakich zespołach ta osoba już jest — kontekst przy wyborze, nie ozdoba. */
function Teams({ user }: { user: UserSummary }) {
  const { t } = useTranslation();

  return user.teams.length === 0 ? (
    <>{t('settings.teams.inNoTeam')}</>
  ) : (
    <>{user.teams.map((team) => team.name).join(', ')}</>
  );
}

function CreateTeamForm({ onCreated }: { onCreated: (team: TeamSummary) => void }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const create = useCreateTeam();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  // ⭐ Dopóki człowiek nie tknie pola klucza, klucz JEDZIE ZA NAZWĄ. Po pierwszej ręcznej
  //    zmianie przestaje — inaczej podpowiedź kasowałaby to, co ktoś właśnie wpisał.
  const [keyTknietyRecznie, setKeyTknietyRecznie] = useState(false);
  const [touched, setTouched] = useState(false);

  const kluczDoWyslania = (keyTknietyRecznie ? key : suggestTeamKey(name)).trim();

  const nameError = touched && name.trim() === '' ? t('settings.teams.nameRequired') : undefined;
  const keyError = touched && kluczDoWyslania === '' ? t('settings.teams.keyRequired') : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if (name.trim() === '' || kluczDoWyslania === '') {
      return;
    }

    create.mutate(
      { name: name.trim(), key: kluczDoWyslania },
      {
        onSuccess: (team) => {
          setName('');
          setKey('');
          setKeyTknietyRecznie(false);
          setTouched(false);
          onCreated(team);
          show({ message: t('settings.teams.created', { name: team.name }), tone: 'success' });
        },
        onError: (failure) => show({ message: describeError(failure), tone: 'danger' }),
      },
    );
  }

  return (
    <section className={styles['card']}>
      <header className={styles['cardHead']}>
        <h2 className={styles['cardTitle']}>{t('settings.teams.createTitle')}</h2>
      </header>

      <div className={styles['cardBody']}>
        <form className={styles['stackedForm']} onSubmit={onSubmit} noValidate>
          {/* ⚠ Wyrównanie do góry — powód przy `.fieldRow` w arkuszu. */}
          <div className={styles['fieldRow']}>
            <Field
              label={t('settings.teams.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              hint={t('settings.teams.nameHint')}
              {...(nameError ? { error: nameError } : {})}
            />

            {/*
              ⭐ Klucz proponuje się na żywo z nazwy, ale ⛔ NIE JEST kopią reguły serwera:
              to podpowiedź, a rozstrzyga serwer i odmawia kodem niosącym wzorzec. Kopia reguły
              na froncie rozjechała się już raz — próg długości hasła, dec. 153.
            */}
            <Field
              label={t('settings.teams.key')}
              value={keyTknietyRecznie ? key : suggestTeamKey(name)}
              hint={t('settings.teams.keyHint')}
              onChange={(event) => {
                setKeyTknietyRecznie(true);
                setKey(event.target.value);
              }}
              {...(keyError ? { error: keyError } : {})}
            />
          </div>

          <div className={styles['formActions']}>
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {t('settings.teams.create')}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
